/**
 * ActivityFeed — recent activity list driven by useTrustAuditLog.
 *
 * Reads the PER-TRUST audit endpoint (`GET /v1/app/trusts/{id}/audit` via
 * `useTrustAuditLog`, the same call the trust's own Audit tab uses) scoped to
 * the user's primary trust (first result from useTrusts), sliced to 5 events.
 * QA finding (2026-07-16): this used to call the tenant-wide `useAuditLog`
 * (top 25 events across every trust) and filter client-side by
 * `subjectId === trustId` — silently wrong/empty on a multi-trust firm
 * account once other trusts' events pushed this trust's out of the top-25
 * window, and it only ever matched `subjectType: 'trust'` events, missing
 * document uploads / resolution signings / etc. tagged to the trust via
 * `metadata.trustId`. `useTrustAuditLog` does the real server-side scoping
 * (see `trusts-audit-log-list.ts`'s two-pronged match). The link target uses
 * the same trust id — no hardcoded fixture id.
 *
 * Accessibility (WCAG 2.2 AA SC 1.1.1, Task 3.21):
 *  Each role-coloured initials avatar carries `role="img"` and an
 *  `aria-label` derived from the actor's role
 *  ("Administrator avatar", "Relationship manager avatar", …). Without
 *  this, screen readers announce just the two-letter initials and lose
 *  the role context that sighted users get from the brass/teal/ink tone.
 *  Mapping intentionally mirrors `routes/admin/dashboard.tsx`.
 */

import { Link } from '@tanstack/react-router';
import { useTrusts, useTrustAuditLog } from '../../lib/api';
import { labelForEvent, labelForEventSubject } from '../../lib/labels';
import { useRoleLabel } from '../../lib/labels/use-role-label';
import { mapError } from '../../lib/error-messages';
import { initialsOf } from '../../lib/format/initials';

interface AvatarProps {
  initials: string;
  color: string;
  ariaLabel: string;
  size?: number;
}

function Avatar({ initials, color, ariaLabel, size = 36 }: AvatarProps) {
  return (
    <div
      className={'avatar ' + color}
      role="img"
      aria-label={ariaLabel}
      style={{ width: size, height: size, fontSize: size <= 30 ? 11 : 13 }}
    >
      {initials}
    </div>
  );
}

function colorOf(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
  const palette = ['ink', 'teal-soft', 'brass', ''];
  return palette[Math.abs(h) % palette.length] ?? '';
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const now = Date.now();
  const delta = now - d.getTime();
  const mins = Math.floor(delta / 60_000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return d.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' });
}

export function ActivityFeed() {
  const trustsQ = useTrusts();
  const trustId = trustsQ.data?.trusts[0]?.id;
  const auditQ = useTrustAuditLog({ trustId, limit: 5 });
  // One resolver for the visible name AND the avatar's aria-label, so a sighted
  // reader and a screen-reader user hear the firm's own role titles alike. The
  // local ROLE_LABELS copy that used to sit here disagreed with nothing today,
  // but it was one of seven copies — the reason a firm rename had nowhere to land.
  const roleLabel = useRoleLabel();

  const events = auditQ.data?.audit ?? [];

  return (
    <div className="card">
      <div className="card-head">
        <div>
          <div className="eyebrow">On the record</div>
          <h2 className="card-title">Recent activity</h2>
        </div>
        {trustId && (
          <Link
            to={`/app/trusts/${encodeURIComponent(trustId)}/audit` as never}
            className="card-link"
            style={{ textDecoration: 'none' }}
          >
            Full audit log →
          </Link>
        )}
      </div>
      {auditQ.isLoading ? (
        <p style={{ color: 'var(--ink-3)', fontSize: 13 }} aria-live="polite">
          Loading the latest audit-log entries&hellip;
        </p>
      ) : auditQ.isError ? (
        // XCUT-08: a fetch failure must read as an ERROR, not "no activity".
        <p role="alert" style={{ color: 'var(--negative)', fontSize: 13, lineHeight: 1.55 }}>
          {mapError(auditQ.error, 'load')}
        </p>
      ) : events.length === 0 ? (
        <p style={{ color: 'var(--ink-3)', fontSize: 13, lineHeight: 1.55 }}>
          No recent activity on record. New events appear here as they
          land in the audit log.
        </p>
      ) : (
        <div className="activity-list">
          {events.map((e) => (
            <div key={e.id} className="activity-item">
              <Avatar
                initials={initialsOf(roleLabel(e.performedByRole || 'system'))}
                color={colorOf(e.performedByRole || 'system')}
                ariaLabel={`${roleLabel(e.performedByRole || 'system')} avatar`}
                size={36}
              />
              <div style={{ flex: 1 }}>
                <div className="activity-body">
                  {/* TRUSTEE-08: render friendly labels, not raw role + dotted
                      action codes (e.g. "Administrator · Trust created"). */}
                  <b>{roleLabel(e.performedByRole || 'system')}</b>{' '}
                  <span style={{ fontSize: 12.5, color: 'var(--ink-2)' }}>
                    {labelForEvent(e.action)}
                  </span>
                </div>
                <div className="activity-time">{formatTime(e.ts)}</div>
              </div>
              {labelForEventSubject(e.subjectType) ? (
                <div className="activity-pill">{labelForEventSubject(e.subjectType)}</div>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
